import React, { useRef, useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { RegExpMatcher, englishDataset, englishRecommendedTransformers } from 'obscenity';

import SelectedProduct from '../../entities/SelectedProduct';
import { buy, resetErrorOnBuy, updateProjectTitle } from '../../actions';
import BuilderMoney from '../BuilderMoney';

const ProjectPurchase = ({ builds = [], buying = false, dispatch, state, setEnableBuilder, currentBuildIndex }) => {
    const modal = useRef(null);
    const currentBuild = builds[currentBuildIndex];
    const initialTitle = currentBuild?.projectTitle ? currentBuild.projectTitle : '';
    const [titleInput, setTitleInput] = useState(initialTitle);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [hasLanguageWarning, setHasLanguageWarning] = useState(false);
    const originalBodyOverflow = useRef();
    const matcher = new RegExpMatcher({
        ...englishDataset.build(),
        ...englishRecommendedTransformers,
    });

    const handleAddToCart = () => {
        if (!currentBuild) return;
        dispatch(buy([currentBuild]));
    };

    useEffect(() => {
        if (state.errorMessage || state.errorOnBuy) {
            if (modal.current && !modal.current.open) {
                modal.current.showModal();
                setIsDialogOpen(true);
            }
        }
    }, [state.errorMessage, state.errorOnBuy]);

    useEffect(() => {
        const freshTitle = currentBuild?.projectTitle ? currentBuild.projectTitle : '';
        setTitleInput(freshTitle);
    }, [currentBuild?.projectTitle]);

    useEffect(() => {
        if (state.finished && !state.errorOnBuy) {
            try {
                if (modal.current && modal.current.open) {
                    modal.current.close();
                    setIsDialogOpen(false);
                }
            } catch (e) {
                /* noop */
            }
        }
    }, [state.finished, state.errorOnBuy]);

    useEffect(() => {
        if (isDialogOpen) {
            if (originalBodyOverflow.current === undefined) {
                originalBodyOverflow.current = document.body.style.overflow;
            }
            document.body.style.overflow = 'hidden';
        } else {
            if (originalBodyOverflow.current !== undefined) {
                document.body.style.overflow = originalBodyOverflow.current;
            } else {
                document.body.style.overflow = '';
            }
        }

        return () => {
            if (originalBodyOverflow.current !== undefined) {
                document.body.style.overflow = originalBodyOverflow.current;
            }
        };
    }, [isDialogOpen]);

    useEffect(() => {
        const dialogEl = modal.current;
        if (!dialogEl) return;
        const handleClose = () => setIsDialogOpen(false);
        dialogEl.addEventListener('close', handleClose);
        dialogEl.addEventListener('cancel', handleClose);
        return () => {
            dialogEl.removeEventListener('close', handleClose);
            dialogEl.removeEventListener('cancel', handleClose);
        };
    }, []);

    const price = useMemo(() => {
        return SelectedProduct.calculateCost(currentBuild?.selected);
    }, [currentBuild]);

    const buildIssues = useMemo(() => {
        if (!currentBuild) return { missing: true };
        const missingHead = !currentBuild.selected.heads;
        const missingBody = !currentBuild.selected.bodies;
        const missingLeg = !currentBuild.selected.legs;
        const missingTitle = !currentBuild.projectTitle;
        return {
            missingHead,
            missingBody,
            missingLeg,
            missingTitle,
            hasAny: missingHead || missingBody || missingLeg || missingTitle,
        };
    }, [currentBuild]);

    return (
        <div className="purchase-container">
            {/* <div className="project__list__add_new_project">
                <button className="button btn span" onClick={handleAddBuild}>
                    <span>Add new project</span>
                </button>
            </div> */}
            <div className="project__add-to-basket">
                <button
                    className="btn btn-pill"
                    onClick={() => {
                        if (modal.current && !modal.current.open) {
                            modal.current.showModal();
                            setIsDialogOpen(true);
                        }
                        dispatch(resetErrorOnBuy());
                    }}
                >
                    Add to cart
                </button>
            </div>
            <dialog ref={modal} className="project_switcher-add-to-basket-modal single-build">
                <h2>Add fig to your cart</h2>
                {/* Project title update form field here? */}
                {currentBuild && (
                    <div className="project_switcher-single-build-summary">
                        <div className="project_switcher__titleAndWarnings">
                            <div className="project_switcher__titleEdit">
                                <label htmlFor="project_switcher_title_input">Project title</label>
                                <input
                                    className="project_switcher_title_input"
                                    id="project_switcher_title_input"
                                    type="text"
                                    value={titleInput}
                                    placeholder="Project title"
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (matcher.hasMatch(val)) {
                                            console.log('The input text contains profanities.');
                                            setHasLanguageWarning(true);
                                        } else {
                                            setHasLanguageWarning(false);
                                        }
                                        setTitleInput(val);
                                        dispatch(updateProjectTitle(currentBuildIndex, val || ''));
                                    }}
                                />
                            </div>
                            <div className="project_switcher__warnings">
                                <ProjectWarnings
                                    projectTitle={currentBuild.projectTitle}
                                    missingHead={buildIssues.missingHead}
                                    missingBody={buildIssues.missingBody}
                                    missingLeg={buildIssues.missingLeg}
                                    hasLanguageWarning={hasLanguageWarning}
                                />
                            </div>
                        </div>
                        <div className="project_switcher__price">
                            Cost: <BuilderMoney amount={price} />
                        </div>
                    </div>
                )}
                {state.errorMessage && (
                    <div className="project_switcher-add-to-basket-warning">
                        {state.errorMessage?.map((error) => (
                            <span key={error}>{error}</span>
                        ))}
                        {state.addedProjects?.length > 0 && state.addedProjects && (
                            <div>
                                <span>Project added to your cart:</span>
                                <ul>
                                    {state.addedProjects?.map((project) => (
                                        <li key={project}>{project}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
                <div className="project_switcher-add-to-basket-actions">
                    <button
                        className="project_switcher-add-to-basket-actions__close"
                        onClick={() => {
                            setEnableBuilder(true);
                            if (modal.current && modal.current.open) {
                                modal.current.close();
                            }
                            setIsDialogOpen(false);
                        }}
                    >
                        <span>Back</span>
                    </button>
                    <div className="project_switcher-add-to-basket-actions__buy">
                        <button
                            className="button"
                            onClick={handleAddToCart}
                            disabled={buying || buildIssues.hasAny || !titleInput.trim()}
                        >
                            <span>{buying ? 'Adding…' : 'Add to cart'}</span>
                        </button>
                    </div>
                </div>
            </dialog>
        </div>
    );
};

ProjectPurchase.propTypes = {
    builds: PropTypes.arrayOf(
        PropTypes.shape({
            projectTitle: PropTypes.string,
            selected: PropTypes.shape({
                heads: PropTypes.any,
                bodies: PropTypes.any,
                legs: PropTypes.any,
                hatsAndHair: PropTypes.any,
            }),
        })
    ),
    buying: PropTypes.bool,
    dispatch: PropTypes.func.isRequired,
    state: PropTypes.shape({
        errorMessage: PropTypes.arrayOf(PropTypes.string),
        errorOnBuy: PropTypes.bool,
        finished: PropTypes.bool,
        addedProjects: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    setEnableBuilder: PropTypes.func.isRequired,
    currentBuildIndex: PropTypes.number.isRequired,
};

const mapStateToProps = (state) => {
    return {
        currentBuildIndex: state.currentBuildIndex,
        builds: state.builds,
        buying: state.buying,
        state,
    };
};

export default connect(mapStateToProps)(ProjectPurchase);

const ProjectWarnings = ({
    projectTitle = undefined,
    missingHead = false,
    missingBody = false,
    missingLeg = false,
    hasLanguageWarning = false,
}) => {
    return (
        <>
            {hasLanguageWarning && (
                <div className="project_switcher__warnings--text">
                    <span>Please reconsider the language used in your project title</span>
                </div>
            )}
            {!projectTitle && (
                <div className="project_switcher__warnings--text">
                    <span>Project needs a title before you can add to cart</span>
                </div>
            )}
            {missingHead && (
                <div className="project_switcher__warnings--text">
                    <span>This project is missing a head piece</span>
                </div>
            )}
            {missingBody && (
                <div className="project_switcher__warnings--text">
                    <span>This project is missing a body piece</span>
                </div>
            )}
            {missingLeg && (
                <div className="project_switcher__warnings--text">
                    <span>This project is missing a legs piece</span>
                </div>
            )}
        </>
    );
};

ProjectWarnings.propTypes = {
    projectTitle: PropTypes.string,
    missingHead: PropTypes.bool,
    missingBody: PropTypes.bool,
    missingLeg: PropTypes.bool,
    hasLanguageWarning: PropTypes.bool,
};
